import React, { useState } from "react";
import { toast } from "sonner";
import { Package, Download, FileText, Tag } from "lucide-react";
import NumberInput from "./NumberInput";
import { openAuthenticatedPdfPost } from "../utils/pdf";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * 4×6" thermal label print dialog.
 *
 * Generates one of:
 *  - Per-item product labels + per-package summary labels (Genera e stampa)
 *  - Items-summary label (Riepilogo articoli)
 *  - Summary-only per-package labels (Solo totale colli)
 *
 * The "Totale etichette" preview matches the backend exactly:
 *   total = sum(perItemQty) + (includeSummary ? max(1, sum(package_count ?? 1)) : 0)
 */
const LabelsPrintDialog = ({ open, onClose, order, items }) => {
  // perItemQty: map of item_key -> quantity (default = package_count from order)
  const initPerItemQty = (its) => {
    const obj = {};
    (its || []).forEach((it) => {
      const k = it.product_id || it.code;
      if (!k) return;
      obj[k] = parseInt(it.package_count) || 1;
    });
    return obj;
  };
  const [perItemQty, setPerItemQty] = useState(() => initPerItemQty(items));
  const [selectedIds, setSelectedIds] = useState(
    () => new Set((items || []).map((it) => it.product_id || it.code).filter(Boolean))
  );
  const [includeSummary, setIncludeSummary] = useState(true);
  const [nameCopies, setNameCopies] = useState(1);
  const [generating, setGenerating] = useState(false);
  // Reset internal state when the dialog re-opens. Done during render (React-recommended
  // pattern) instead of useEffect to avoid `set-state-in-effect`.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (open) {
      setSelectedIds(new Set((items || []).map((it) => it.product_id || it.code).filter(Boolean)));
      setPerItemQty(initPerItemQty(items));
    }
  }

  const toggleAll = () => {
    if (selectedIds.size === items.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((it) => it.product_id || it.code).filter(Boolean)));
  };
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };
  const setQty = (id, n) => {
    setPerItemQty({ ...perItemQty, [id]: Math.max(0, parseInt(n) || 0) });
  };

  // Mirror backend: per-package summary count, with min 1 if include_summary on.
  // Backend (order_labels.py): pkg_total = sum(it.package_count or 1 if not None else 1);
  // if pkg_total < 1: pkg_total = 1.
  const summaryLabels = (() => {
    if (!includeSummary) return 0;
    const total = (items || []).reduce((s, it) => {
      const raw = it.package_count;
      const n = raw === null || raw === undefined || raw === "" ? 1 : parseInt(raw);
      return s + (isNaN(n) ? 1 : n);
    }, 0);
    return Math.max(1, total);
  })();
  const itemLabels = Array.from(selectedIds).reduce(
    (s, id) => s + (parseInt(perItemQty[id]) || 0),
    0
  );
  const totalLabels = itemLabels + summaryLabels;

  const generate = async (mode = "labels") => {
    setGenerating(true);
    try {
      let url;
      let body = null;
      if (mode === "summary") {
        url = `${API}/orders/${order.id}/summary-label-pdf`;
      } else if (mode === "customer-name") {
        url = `${API}/orders/${order.id}/customer-name-label-pdf?copies=${Math.max(1, parseInt(nameCopies) || 1)}`;
      } else if (mode === "items-summary") {
        url = `${API}/orders/${order.id}/items-summary-label-pdf`;
      } else {
        url = `${API}/orders/${order.id}/labels-pdf`;
        body = {
          items: Array.from(selectedIds)
            .map((id) => ({ item_key: id, quantity: parseInt(perItemQty[id]) || 0 }))
            .filter((r) => r.quantity > 0),
          include_summary: includeSummary,
        };
      }
      const ok = await openAuthenticatedPdfPost(url, body, { filename: `Etichette-${order?.order_number || "ordine"}.pdf` });
      if (ok) toast.success("Etichette pronte per la stampa");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore generazione etichette");
    } finally {
      setGenerating(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto"
        data-testid="labels-print-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-stone-900" /> Stampa etichette 4×6&quot;
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-stone-50 p-3 rounded-md border border-stone-200">
            <p className="text-sm font-semibold">
              Cliente:{" "}
              <span className="text-stone-900">
                {order.customer?.business_name ||
                  order.customer?.customer_name ||
                  order.customer?.name ||
                  order.customer?.email ||
                  "—"}
              </span>
            </p>
            <p className="text-xs text-stone-600 mt-1">
              Ordine: <span className="font-mono">{order.order_number}</span>
            </p>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Articoli da etichettare</Label>
            <Button size="sm" variant="outline" onClick={toggleAll} className="h-7 text-xs">
              {selectedIds.size === items.length ? "Deseleziona tutti" : "Seleziona tutti"}
            </Button>
          </div>

          <div className="max-h-64 overflow-y-auto border border-stone-200 rounded-md divide-y divide-stone-100">
            {items.map((it, idx) => {
              const id = it.product_id || it.code;
              const isSel = selectedIds.has(id);
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-3 p-2.5 ${isSel ? "bg-stone-50" : ""}`}
                  data-testid={`label-item-${it.code}`}
                >
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggleOne(id)}
                    className="w-4 h-4 accent-stone-900 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0" onClick={() => toggleOne(id)}>
                    <p className="text-xs font-mono text-stone-500">{it.code}</p>
                    <p className="text-sm font-medium truncate">{it.description}</p>
                  </div>
                  <span className="text-xs text-stone-500 shrink-0 hidden sm:block">
                    {(it.actual_quantity ?? it.quantity ?? 0).toFixed(2)} {it.unit || "Kg"}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    <NumberInput
                      min="0"
                      max="50"
                      step="1"
                      value={perItemQty[id] ?? 1}
                      onChange={(e) => setQty(id, e.target.value)}
                      disabled={!isSel}
                      className="h-8 w-14 text-center bg-white border-stone-300"
                      data-testid={`label-qty-${it.code}`}
                    />
                    <span className="text-xs text-stone-400">et.</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="includeSummary"
              checked={includeSummary}
              onChange={(e) => setIncludeSummary(e.target.checked)}
              className="w-4 h-4 accent-stone-900"
              data-testid="labels-include-summary"
            />
            <Label htmlFor="includeSummary" className="text-sm cursor-pointer">
              Includi etichette riepilogo colli (N/M)
            </Label>
          </div>

          <div className="bg-stone-100 p-3 rounded-md text-sm flex items-center justify-between flex-wrap gap-2">
            <span className="text-stone-600">
              Totale etichette: <strong className="text-stone-900" data-testid="labels-total-count">{totalLabels}</strong>
              {includeSummary && (
                <span className="text-xs text-stone-500 ml-1">
                  ({itemLabels} prodotto + {summaryLabels} riepilogo)
                </span>
              )}
            </span>
            <span className="text-xs text-stone-500">Formato 4×6&quot; (101.6×152.4 mm)</span>
          </div>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => generate("items-summary")}
            disabled={generating}
            className="border-stone-400 text-stone-700"
            data-testid="generate-items-summary-btn"
          >
            <FileText className="w-4 h-4 mr-1" /> Riepilogo articoli
          </Button>
          <Button
            variant="outline"
            onClick={() => generate("summary")}
            disabled={generating}
            className="border-stone-400 text-stone-700"
            data-testid="generate-summary-only-btn"
          >
            <Package className="w-4 h-4 mr-1" /> Solo totale colli
          </Button>
          <div className="inline-flex items-center gap-1" data-testid="customer-name-label-group">
            <Button
              variant="outline"
              onClick={() => generate("customer-name")}
              disabled={generating}
              className="border-stone-400 text-stone-700"
              data-testid="generate-customer-name-btn"
              title="Etichetta 4×6 con il solo nome del cliente"
            >
              <Tag className="w-4 h-4 mr-1" /> Solo nome cliente
            </Button>
            <input
              type="number"
              min={1}
              max={50}
              value={nameCopies}
              onChange={(e) => setNameCopies(e.target.value)}
              className="w-14 h-9 border border-stone-300 rounded-md px-2 text-sm text-center bg-white"
              title="Copie"
              data-testid="customer-name-copies-input"
            />
          </div>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose} className="border-stone-300">
            Chiudi
          </Button>
          <Button
            onClick={() => generate("labels")}
            disabled={generating || selectedIds.size === 0 || totalLabels === 0}
            className="bg-stone-900 hover:bg-black text-white"
            data-testid="generate-labels-btn"
          >
            <Download className="w-4 h-4 mr-1" />{" "}
            {generating ? "Generazione..." : "Genera e stampa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LabelsPrintDialog;
